/**
 * The leagues this app can compute a baseline for.
 *
 * Ported verbatim from `LeagueStatsService.Known`. **The codes are NOT guessable** — an earlier
 * pass invented plausible ones ("lpa", "csl", "uecl") and every single league silently bound to
 * nothing. The `code` is the observed Polymarket slug prefix and the `espnSlug` was verified to
 * return real matches; a league is registered only when both sides are confirmed.
 *
 * Note especially: `col` is the Europa CONFERENCE League while `col1` is Colombia, and `chi` is
 * CHINA while `chi1` is CHILE. These are the venue's own abbreviations, not country codes.
 *
 * `tagId` is only used by the settled-market path, which is secondary — the ESPN backfill is the
 * primary source and does not need it, so `''` is acceptable.
 */
export interface LeagueDefinition {
  code: string;
  displayName: string;
  tagId: string;
  espnSlug: string;
}

export const LEAGUES: readonly LeagueDefinition[] = [
  { code: 'mls', displayName: 'MLS', tagId: '100100', espnSlug: 'usa.1' },
  { code: 'bra', displayName: 'Brazil Série A', tagId: '', espnSlug: 'bra.1' },
  { code: 'uslc', displayName: 'USL Championship', tagId: '', espnSlug: 'usa.usl.1' },
  { code: 'ucl', displayName: 'UEFA Champions League', tagId: '', espnSlug: 'uefa.champions' },
  { code: 'uel', displayName: 'UEFA Europa League', tagId: '', espnSlug: 'uefa.europa' },
  { code: 'sud', displayName: 'Copa Sudamericana', tagId: '', espnSlug: 'conmebol.sudamericana' },
  { code: 'col', displayName: 'Europa Conference League', tagId: '', espnSlug: 'uefa.europa.conf' },
  { code: 'arg', displayName: 'Primera División Argentina', tagId: '', espnSlug: 'arg.1' },
  { code: 'argpn', displayName: 'Primera Nacional', tagId: '', espnSlug: 'arg.2' },
  { code: 'mex', displayName: 'Liga MX', tagId: '', espnSlug: 'mex.1' },
  { code: 'col1', displayName: 'Colombia Primera A', tagId: '', espnSlug: 'col.1' },
  { code: 'chi', displayName: 'Chinese Super League', tagId: '', espnSlug: 'chn.1' },
  { code: 'chi1', displayName: 'Chile Primera División', tagId: '', espnSlug: 'chi.1' },
  { code: 'per1', displayName: 'Peru Liga 1', tagId: '', espnSlug: 'per.1' },
  { code: 'uru1', displayName: 'Primera División (Uruguay)', tagId: '', espnSlug: 'uru.1' },
  { code: 'ecu1', displayName: 'Ecuador LigaPro', tagId: '', espnSlug: 'ecu.1' },
  { code: 'rus', displayName: 'Russian Premier League', tagId: '', espnSlug: 'rus.1' },
  { code: 'nor', displayName: 'Norway Eliteserien', tagId: '', espnSlug: 'nor.1' },
  { code: 'swe', displayName: 'Sweden Allsvenskan', tagId: '', espnSlug: 'swe.1' },
  { code: 'den', displayName: 'Denmark Superliga', tagId: '', espnSlug: 'den.1' },
  { code: 'scop', displayName: 'Scottish Premiership', tagId: '', espnSlug: 'sco.1' },
  { code: 'aut', displayName: 'Austria Bundesliga', tagId: '', espnSlug: 'aut.1' },
  { code: 'nwsl', displayName: 'NWSL', tagId: '', espnSlug: 'usa.nwsl' },
];

export function findLeague(code: string | null | undefined): LeagueDefinition | undefined {
  if (!code) return undefined;
  const lower = code.toLowerCase();
  return LEAGUES.find((l) => l.code.toLowerCase() === lower);
}

/**
 * Strips combining marks so "Brazil Serie A" from gamma matches "Brazil Série A" as registered.
 *
 * A plain `contains` silently unhooks the whole league — no baseline, no league %, tab disabled —
 * and it did. Every league-name comparison must go through {@link nameMatches}.
 */
function stripDiacritics(s: string): string {
  return s.normalize('NFD').replace(/\p{Mn}/gu, '').normalize('NFC');
}

export function nameMatches(polymarketLeagueName: string, league: LeagueDefinition): boolean {
  if (!polymarketLeagueName?.trim()) return false;
  return stripDiacritics(polymarketLeagueName)
    .toLowerCase()
    .includes(stripDiacritics(league.displayName).toLowerCase());
}

/**
 * Resolves a match to its league definition by SLUG PREFIX (`arg2-ddb-est-2026-08-01` -> `arg2`).
 * The slug is machine-generated and exact, whereas display names carry season suffixes and accents
 * that silently broke matching before. Name matching stays only as a fallback.
 */
export function definitionFor(slug: string, leagueName: string): LeagueDefinition | undefined {
  const code = (slug || '').split('-')[0];
  return findLeague(code) ?? LEAGUES.find((l) => nameMatches(leagueName, l));
}

/** Pulls the two team codes out of a match slug like `mls-nyr-clt-2026-07-25`. */
export function parseTeamCodes(slug: string): { home: string | null; away: string | null } {
  const parts = (slug || '').split('-');
  return parts.length >= 3 ? { home: parts[1], away: parts[2] } : { home: null, away: null };
}
